/**
 * SubtitleView — live subtitle surface for the host console.
 *
 * Renders lines from the subtitle store, styling in-progress (partial) lines
 * distinctly from finalized ones, and auto-scrolls to the newest line unless
 * the user has scrolled up to read history.
 */

import { useEffect, useRef, type ReactElement } from 'react';
import {
  useSubtitleStore,
  type SubtitleStore,
} from '@/features/subtitles/subtitleStore';

export interface SubtitleViewProps {
  /**
   * Which subtitle store instance to render. Defaults to the shared global
   * store (host console). The viewer page passes its own isolated instance.
   */
  store?: SubtitleStore;
  /** Empty-state message shown before any line arrives. */
  emptyLabel?: string;
}

export function SubtitleView({
  store = useSubtitleStore,
  emptyLabel = 'As legendas aparecerão aqui ao iniciar a captura.',
}: SubtitleViewProps = {}): ReactElement {
  const lines = store((s) => s.lines);
  const scrollRef = useRef<HTMLDivElement>(null);
  const pinnedToBottom = useRef(true);

  // Track whether the user is pinned to the bottom (auto-scroll) or reading up.
  function handleScroll(): void {
    const el = scrollRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    pinnedToBottom.current = distanceFromBottom < 48;
  }

  useEffect(() => {
    const el = scrollRef.current;
    if (el && pinnedToBottom.current) {
      el.scrollTop = el.scrollHeight;
    }
  }, [lines]);

  return (
    <div
      ref={scrollRef}
      onScroll={handleScroll}
      style={styles.container}
      aria-live="polite"
      aria-atomic="false"
    >
      {lines.length === 0 ? (
        <p style={styles.empty}>{emptyLabel}</p>
      ) : (
        lines.map((line) => (
          <div key={line.segmentId} style={styles.lineBlock}>
            {line.original && (
              <p style={styles.original} lang={line.sourceLang}>
                {line.original}
              </p>
            )}
            <p
              lang={line.targetLang}
              style={{
                ...styles.line,
                ...(line.final ? styles.final : styles.partial),
              }}
            >
              {line.text}
            </p>
          </div>
        ))
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    flex: 1,
    minHeight: 240,
    overflowY: 'auto',
    padding: '1rem 1.25rem',
    background: 'var(--rt-bg)',
    border: '1px solid var(--rt-border)',
    borderRadius: 12,
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    scrollBehavior: 'smooth',
  },
  empty: { margin: 0, color: 'var(--rt-text-muted)', fontSize: '0.9rem' },
  lineBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.15rem',
  },
  original: {
    margin: 0,
    fontSize: '0.8rem',
    lineHeight: 1.3,
    color: 'var(--rt-text-muted)',
    opacity: 0.85,
  },
  line: {
    margin: 0,
    fontSize: '1.15rem',
    lineHeight: 1.45,
  },
  final: { color: 'var(--rt-text)' },
  partial: { color: 'var(--rt-text-muted)', fontStyle: 'italic' },
};
